/**
 * Hook aislado para gestionar marcadores de un documento.
 * Carga la lista una vez (cuando documentId está listo) y expone
 * acciones optimistas para crear/editar/eliminar.
 *
 * No toca el motor del visor.
 */
import { useEffect, useState, useCallback } from 'react';
import { marcadoresAPI } from '../services/api';

const useMarcadores = (documentId, isOpen) => {
  const [marcadores, setMarcadores] = useState([]);
  const [loading, setLoading] = useState(false);

  const recargar = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const { data } = await marcadoresAPI.listar(documentId);
      setMarcadores(Array.isArray(data) ? data : []);
    } catch (_err) {
      setMarcadores([]);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  // Cargar al abrir el visor o cambiar de documento
  useEffect(() => {
    if (!isOpen || !documentId) {
      setMarcadores([]);
      return;
    }
    recargar();
  }, [isOpen, documentId, recargar]);

  const crear = useCallback(
    async ({ pagina, nota, compartido }) => {
      if (!documentId || !pagina) return null;
      try {
        const { data } = await marcadoresAPI.crear({
          manual_id: documentId,
          pagina,
          nota: nota || null,
          compartido: !!compartido,
        });
        setMarcadores((prev) =>
          [...prev, data].sort((a, b) => a.pagina - b.pagina)
        );
        return data;
      } catch (_err) {
        return null;
      }
    },
    [documentId]
  );

  const actualizar = useCallback(async (id, patch) => {
    try {
      const { data } = await marcadoresAPI.actualizar(id, patch);
      setMarcadores((prev) =>
        prev.map((m) => (m.id === id ? data : m)).sort((a, b) => a.pagina - b.pagina)
      );
      return data;
    } catch (_err) {
      return null;
    }
  }, []);

  const eliminar = useCallback(async (id) => {
    // Optimistic
    const prev = marcadores;
    setMarcadores((arr) => arr.filter((m) => m.id !== id));
    try {
      await marcadoresAPI.eliminar(id);
      return true;
    } catch (_err) {
      // Rollback
      setMarcadores(prev);
      return false;
    }
  }, [marcadores]);

  return { marcadores, loading, crear, actualizar, eliminar, recargar };
};

export default useMarcadores;
